import { useCallback, useEffect, useRef, useState } from 'react';

import { AccountViewListingGroupType } from '@zg-rentals/ts-rental-manager-api-beta-client';
import { Box, Table, VisuallyHidden } from '@zillow/constellation-10';

import PropertiesPageCSEvents from '../../../model/ClickstreamEvent/PropertiesPageCSEvents';
import PropertyConstants from '../../../constants/PropertyConstants';
import { useSelector } from '../../../selectors/useSelector';
import { AppSelectors, PropertiesSelectors } from '../../../selectors';
import { FixedScrollbar } from '../../../containers/ZRAMPropertiesPage/components/PropertyTable/FixedScrollbar';
import { getCurrentMonthString } from '../../../utils/dateUtils';

import { transformFiltersToApiParams } from '../../../hooks/queries/zram';
import { useGetMultiplexUnits } from '../../../hooks/queries/properties/useGetMultiplexUnits';
import { useTableObserver } from '../../../hooks/useTableObserver';

import GroupablePropertyRow from '../GroupablePropertyRow';

import { stickyLeftCellRecipe, TableHeaderCell } from '../GroupablePropertyRow/styles';

import { type Dispatch, type SetStateAction } from 'react';

import type {
  AccountViewListingGroupDto,
  AccountViewMultiplexUnitDto,
  WebAnalyticsLeaseDto,
} from '@zg-rentals/ts-rental-manager-api-beta-client';

import type { ZrmClickstreamListing } from '@zg-rentals/clickstream';

const { PROPERTY_TABLE_COLUMNS, PROPERTY_TABLE_TRIGGER_OBJECT_MAP } = PropertyConstants;

const emptyListingCount = {
  active: 0,
  archived: 0,
  atRisk: 0,
  freeBonusListingCount: 0,
  freeClaimedListingCount: 0,
  freeListingsOwnedByCustomer: 0,
  inactive: 0,
  total: 0,
};

const emptyAddress = { city: '', state: '', street: '', unit: '', zip: '' };

// Map units to table row shape
function mapUnitToListingGroupRow(unit: AccountViewMultiplexUnitDto): AccountViewListingGroupDto {
  return {
    type: AccountViewListingGroupType.LISTING,
    groupId: unit.groupId,
    listingAlias: unit.listingAlias,
    name: unit.name,
    photoKey: unit.photoKey,
    status: unit.status,
    isMultifamily: false,
    address: emptyAddress,
    listingCount: emptyListingCount,
  };
}

const GroupablePropertiesViewList = ({ listings }: { listings: Array<AccountViewListingGroupDto> }) => {
  const { user, isScreenMobile, propertiesFilters } = useSelector((state) => ({
    user: state.user,
    isScreenMobile: AppSelectors.makeIsScreenMobile(state),
    propertiesFilters: PropertiesSelectors.makePropertiesFilters(state),
  }));

  const filters = {
    searchText: propertiesFilters.searchText ?? '',
    subNavFilterType: propertiesFilters.subNavFilterType ?? 'all',
  };

  // Groups that have been expanded at least once; open vs collapsed is tracked via expandedUnitsByGroupId[].isRowOpen.
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(new Set());

  const expandedGroupIdsArray = Array.from(expandedGroupIds);

  const tableRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [listingsState, setListingsState] = useState<Array<AccountViewListingGroupDto>>(listings);
  const [showMoreListingAlias, setShowMoreListingAlias] = useState('');
  const [hoveredCols, setHoveredCols] = useState<Record<string, boolean>>({});

  /**
   * insertIndex is the index of the first unit added to the group
   * count is the number of units added to the group
   * isRowOpen is true if the group is expanded
   */
  const [expandedUnitsByGroupId, setExpandedUnitsByGroupId] = useState<
    Record<string, { insertIndex: number; count: number; isRowOpen: boolean }>
  >({});

  /** Groups currently collapsing (animation in progress). Only one at a time to keep removeRowsAt indices in sync. */
  const [collapsingGroupIds, setCollapsingGroupIds] = useState<Set<string>>(new Set());

  const groupIdsToFetch = expandedGroupIdsArray.filter((id) => expandedUnitsByGroupId[id]?.isRowOpen === true);

  const {
    dataByGroupId,
    fetchNextPageForGroup,
    hasNextPageByGroupId,
    isFetchingByGroupId,
    isFetchingNextPageByGroupId,
  } = useGetMultiplexUnits(transformFiltersToApiParams(filters), groupIdsToFetch);

  // track row indices currently animating (for add/remove transitions)
  const [animatingIndices, setAnimatingIndices] = useState<Set<number>>(new Set());
  const [removingIndices, setRemovingIndices] = useState<Set<number>>(new Set());

  const { isScrolledToLeft, showFixedScrollbar, isTableBottomVisible } = useTableObserver({
    scrollContainerRef,
    isScreenMobile,
  });

  // returns animation style for rows being added (fade-in) or removed (fade-out)
  const getRowAnimationStyle = (index: number) => {
    if (animatingIndices.has(index)) {
      return { opacity: 0, animation: 'rowFadeIn 0.3s ease-out forwards' };
    }
    if (removingIndices.has(index)) {
      return { opacity: 1, animation: 'rowFadeOut 0.3s ease-out forwards' };
    }
    return undefined;
  };

  // triggers animation on indices, clears after duration, then runs optional callback
  const animateRows = (
    indices: Set<number>,
    setIndices: Dispatch<SetStateAction<Set<number>>>,
    duration: number,
    onComplete?: () => void,
  ) => {
    setIndices(indices);

    setTimeout(() => {
      setIndices(new Set());
      onComplete?.();
    }, duration);
  };

  const addRowsAt = useCallback((insertIndex: number, itemsToAdd: Array<AccountViewListingGroupDto>) => {
    const indices = new Set(itemsToAdd.map((_, i) => insertIndex + i));

    animateRows(indices, setAnimatingIndices, 350);

    setListingsState((prev) => {
      const newListings = [...prev];
      newListings.splice(insertIndex, 0, ...itemsToAdd);

      return newListings;
    });
  }, []);

  const removeRowsAt = useCallback(
    (removeStartIndex: number, removeCount: number, onComplete?: () => void) => {
      if (listingsState.length <= 1 || removeCount <= 0) {
        onComplete?.();
        return;
      }

      const count = Math.min(removeCount, listingsState.length - removeStartIndex);
      const indices = new Set(Array.from({ length: count }, (_, i) => removeStartIndex + i));

      animateRows(indices, setRemovingIndices, 300, () => {
        setListingsState((prev) => {
          const newListings = [...prev];
          newListings.splice(removeStartIndex, count);

          return newListings;
        });
        onComplete?.();
      });
    },
    [listingsState.length],
  );

  // Add unit rows to a group at the given index (triggered when expand is clicked and data is ready).
  const addUnitRowsToGroup = useCallback(
    (groupId: string, insertIndex: number, units: Array<AccountViewMultiplexUnitDto>) => {
      const rowsToAdd = units.map(mapUnitToListingGroupRow);
      addRowsAt(insertIndex, rowsToAdd);

      setExpandedUnitsByGroupId((map) => {
        const next = {
          ...map,
          [groupId]: { insertIndex, count: rowsToAdd.length, isRowOpen: true },
        };
        // When multiple groups expand in one batch we insert high-to-low; each insertion shifts
        // rows that were already added, so keep insertIndex in sync for groups at or after this one.
        for (const otherId of Object.keys(next)) {
          if (otherId === groupId) continue;

          const other = next[otherId]!;

          // keep insertIndex in sync by adding the count of the added rows
          if (other.insertIndex >= insertIndex) {
            next[otherId] = {
              insertIndex: other.insertIndex + rowsToAdd.length,
              count: other.count,
              isRowOpen: other.isRowOpen,
            };
          }
        }
        return next;
      });
    },
    [addRowsAt],
  );

  // Append more unit rows when "Load more units" fetches the next page.
  const addMoreUnitRowsToGroup = useCallback(
    (groupId: string, newUnits: Array<AccountViewMultiplexUnitDto>) => {
      // if the group is collapsing, don't add more units
      if (collapsingGroupIds.has(groupId)) {
        return;
      }

      const entry = expandedUnitsByGroupId[groupId];
      if (!entry?.isRowOpen || newUnits.length === 0) {
        return;
      }

      const { insertIndex, count } = entry;
      const rowsToAdd = newUnits.map(mapUnitToListingGroupRow);
      addRowsAt(insertIndex + count, rowsToAdd);

      setExpandedUnitsByGroupId((map) => {
        const next = { ...map };
        const newCount = count + rowsToAdd.length;
        next[groupId] = { insertIndex, count: newCount, isRowOpen: true };
        for (const otherId of Object.keys(next)) {
          if (otherId === groupId) continue;
          const other = next[otherId]!;
          if (other.insertIndex >= insertIndex + count) {
            next[otherId] = {
              insertIndex: other.insertIndex + rowsToAdd.length,
              count: other.count,
              isRowOpen: other.isRowOpen,
            };
          }
        }
        return next;
      });
    },
    [collapsingGroupIds, expandedUnitsByGroupId, addRowsAt],
  );

  // Remove unit rows from a group (triggered when collapse is clicked).
  const removeUnitRowsFromGroup = useCallback(
    (groupId: string) => {
      const entry = expandedUnitsByGroupId[groupId];
      if (!entry) {
        return;
      }

      const { insertIndex, count } = entry;

      const setCollapsed = () => {
        // remove the group from the collapsing groups
        setCollapsingGroupIds((prev) => {
          const next = new Set(prev);
          next.delete(groupId);
          return next;
        });
        setExpandedUnitsByGroupId((map) => {
          const next = {
            ...map,
            [groupId]: { insertIndex: 0, count: 0, isRowOpen: false },
          };

          for (const otherId of Object.keys(next)) {
            if (otherId === groupId) continue;
            const other = next[otherId]!;

            if (other.insertIndex > insertIndex) {
              next[otherId] = {
                insertIndex: other.insertIndex - count,
                count: other.count,
                isRowOpen: other.isRowOpen,
              };
            }
          }
          return next;
        });
      };

      if (count > 0) {
        // add the group to the collapsing groups
        setCollapsingGroupIds((prev) => new Set(prev).add(groupId));
        removeRowsAt(insertIndex, count, setCollapsed);
      } else {
        setCollapsed();
      }
    },
    [expandedUnitsByGroupId, removeRowsAt],
  );

  const handleExpandGroupClick = useCallback(
    ({ index, groupId }: { index: number; groupId: string }) => {
      // if a group is collapsing, don't expand it
      if (collapsingGroupIds.size > 0) {
        return;
      }

      if (expandedUnitsByGroupId[groupId]?.isRowOpen === true) {
        // --- COLLAPSE: remove unit rows and set isRowOpen false (no need to remove from expandedGroupIds) ---
        removeUnitRowsFromGroup(groupId);
      } else {
        // --- EXPAND: prepare to add unit rows ---
        setExpandedUnitsByGroupId((prev) => ({
          ...prev,
          [groupId]: { insertIndex: index + 1, count: 0, isRowOpen: true },
        }));

        setExpandedGroupIds((prev) => {
          const next = new Set(prev);
          next.add(groupId);

          return next;
        });
      }
    },
    [collapsingGroupIds.size, expandedUnitsByGroupId, removeUnitRowsFromGroup],
  );

  const lastMergedPhotoKeysRef = useRef<string | null>(null);

  // sync local state when parent passes new listings (e.g. after filter change or refetch)
  useEffect(() => {
    lastMergedPhotoKeysRef.current = null;

    setListingsState(listings);
  }, [listings]);

  // Merge new data from dataByGroupId into listingsState so photoKey (and unit rows) stay in sync after refetch (e.g. photo upload)
  useEffect(() => {
    const photoKeysSnapshot: Record<string, Array<string | undefined>> = {};

    for (const groupId of Object.keys(dataByGroupId)) {
      const units = dataByGroupId[groupId]?.units;

      if (units?.length) {
        photoKeysSnapshot[groupId] = units.map((u) => u?.photoKey);
      }
    }

    const snapshotStr = JSON.stringify(photoKeysSnapshot);

    if (lastMergedPhotoKeysRef.current === snapshotStr) {
      return;
    }
    lastMergedPhotoKeysRef.current = snapshotStr;

    setListingsState((prev) => {
      let next: Array<AccountViewListingGroupDto> | null = null;

      for (const groupId of Object.keys(dataByGroupId)) {
        const groupData = dataByGroupId[groupId];
        const units = groupData?.units;
        if (!units?.length) {
          continue;
        }

        const groupRowIndex = prev.findIndex((r) => r.groupId === groupId);

        if (groupRowIndex === -1) {
          continue;
        }

        if (next === null) next = [...prev];

        const newGroupPhotoKey = units[0]?.photoKey;

        if (newGroupPhotoKey != null && next[groupRowIndex]!.photoKey !== newGroupPhotoKey) {
          next[groupRowIndex] = { ...next[groupRowIndex]!, photoKey: newGroupPhotoKey };
        }

        const entry = expandedUnitsByGroupId[groupId];

        if (entry?.isRowOpen && entry.count > 0) {
          for (let i = 0; i < Math.min(entry.count, units.length); i++) {
            const unitRowIndex = groupRowIndex + 1 + i;

            if (unitRowIndex >= next.length) {
              break;
            }

            const unit = units[i];

            if (unit && next[unitRowIndex]!.photoKey !== unit.photoKey) {
              next[unitRowIndex] = { ...next[unitRowIndex]!, photoKey: unit.photoKey };
            }
          }
        }
      }

      return next ?? prev;
    });
  }, [dataByGroupId, expandedUnitsByGroupId]);

  /**
   * When units data loads for expanded groups, add rows via explicit helper (bottom to top so indices stay valid).
   * insertIndex comes from handleExpandGroupClick (index + 1), so we avoid findIndex here.
   * Also appends new rows when "Load more units" fetches the next page (data.units.length > entry.count).
   */
  useEffect(() => {
    const toAdd: Array<{ groupId: string; insertIndex: number; units: Array<AccountViewMultiplexUnitDto> }> = [];
    const toAppend: Array<{ groupId: string; newUnits: Array<AccountViewMultiplexUnitDto> }> = [];

    for (const groupId of expandedGroupIdsArray) {
      if (collapsingGroupIds.has(groupId)) continue;

      const data = dataByGroupId[groupId];
      const entry = expandedUnitsByGroupId[groupId];

      if (!data?.units?.length || !entry?.isRowOpen) continue;
      if (data.units[0]?.groupId !== groupId) continue;

      if (entry.count === 0) {
        toAdd.push({ groupId, insertIndex: entry.insertIndex, units: data.units });
      } else if (data.units.length > entry.count) {
        // has more units to append
        toAppend.push({ groupId, newUnits: data.units.slice(entry.count) });
      }
    }

    if (toAdd.length > 0) {
      toAdd.sort((a, b) => b.insertIndex - a.insertIndex);
      for (const { groupId, insertIndex, units } of toAdd) {
        addUnitRowsToGroup(groupId, insertIndex, units);
      }
    }
    for (const { groupId, newUnits } of toAppend) {
      addMoreUnitRowsToGroup(groupId, newUnits);
    }
  }, [
    expandedGroupIdsArray,
    dataByGroupId,
    expandedUnitsByGroupId,
    collapsingGroupIds,
    addUnitRowsToGroup,
    addMoreUnitRowsToGroup,
  ]);

  const closeOpenMenus = useCallback(
    (e: MouseEvent | KeyboardEvent) => {
      if (tableRef.current && showMoreListingAlias && !tableRef.current.contains(e.target as Node)) {
        setTimeout(() => {
          setShowMoreListingAlias('');
        }, 0);
      }
    },
    [showMoreListingAlias],
  );

  useEffect(() => {
    document.addEventListener('mouseup', closeOpenMenus);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeOpenMenus(e);
      }
    });

    return () => {
      document.removeEventListener('mouseup', closeOpenMenus);
      document.removeEventListener('keydown', closeOpenMenus);
    };
  }, [closeOpenMenus]);

  // Determine which rows should show the "Load more units" button
  const shouldShowLoadMoreForIndex = (index: number, listing: AccountViewListingGroupDto): boolean => {
    const { groupId } = listing;
    const entry = expandedUnitsByGroupId[groupId];

    if (!entry?.isRowOpen || entry.count === 0) {
      return false;
    }

    const lastUnitIndex = entry.insertIndex + entry.count - 1;

    // if the current unit is the last unit in the group and there are more units to load
    return index === lastUnitIndex && hasNextPageByGroupId[groupId];
  };

  const handleUpsellCellHover = (
    columnLabel: string,
    currentProperty: ZrmClickstreamListing,
    lease?: WebAnalyticsLeaseDto,
  ) => {
    // only fire the cs event if we have not already fired one for an cell in this column
    if (!hoveredCols[columnLabel]) {
      PropertiesPageCSEvents.PropertyCardHover({
        user,
        currentProperty,
        lease,
        triggerObject:
          PROPERTY_TABLE_TRIGGER_OBJECT_MAP.HOVER_TRIGGERS[
            columnLabel as keyof typeof PROPERTY_TABLE_TRIGGER_OBJECT_MAP.HOVER_TRIGGERS
          ],
      });
      setHoveredCols((prev) => ({ ...prev, [columnLabel]: true }));
    }
  };

  return (
    <Box
      ref={tableRef}
      css={{
        overflow: { base: 'auto', lg: 'hidden' },
        borderRadius: '200',
        borderBottomRadius: '0',
        borderLeft: '1px solid {colors.Table.Cell.border}',
        borderRight: '1px solid {colors.Table.Cell.border}',
        borderTop: '1px solid {colors.border.subtle}',
      }}
    >
      <Box
        ref={scrollContainerRef}
        css={{
          overflowX: { base: 'hidden', lg: 'auto' },
          maxWidth: '100%',
        }}
      >
        <Table.Root
          appearance="horizontal"
          aria-label="Medium-sized grid table"
          size="md"
          data-testid="GroupablePropertiesViewList"
          css={{ tableLayout: 'fixed' }}
        >
          <Table.Header css={{ display: { base: 'none', lg: 'table-header-group' } }}>
            <Table.Row>
              <TableHeaderCell
                id={PROPERTY_TABLE_COLUMNS.ADDRESS}
                css={stickyLeftCellRecipe.raw({ scrolled: isScrolledToLeft })}
              >
                Address
              </TableHeaderCell>
              <TableHeaderCell id={PROPERTY_TABLE_COLUMNS.RENT}>
                {getCurrentMonthString(new Date())} rent
              </TableHeaderCell>
              <TableHeaderCell id={PROPERTY_TABLE_COLUMNS.POTENTIAL_TENANTS}>Potential tenants</TableHeaderCell>
              <TableHeaderCell id={PROPERTY_TABLE_COLUMNS.LEASES}>Lease</TableHeaderCell>
              <TableHeaderCell id={PROPERTY_TABLE_COLUMNS.ACTIONS}>
                <VisuallyHidden>Actions</VisuallyHidden>
              </TableHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {listingsState.map((listing, i) => (
              <GroupablePropertyRow
                key={`listing-${i}`}
                index={i}
                listing={listing}
                showMoreListingAlias={showMoreListingAlias}
                setShowMoreListingAlias={setShowMoreListingAlias}
                onUpsellCellHover={handleUpsellCellHover}
                rowStyle={getRowAnimationStyle(i)}
                isScrolledToLeft={isScrolledToLeft}
                onExpandGroupClick={handleExpandGroupClick}
                isRowOpen={expandedUnitsByGroupId[listing.groupId]?.isRowOpen ?? false}
                shouldShowLoadMore={shouldShowLoadMoreForIndex(i, listing)}
                dataByGroupId={dataByGroupId}
                fetchNextPageForGroup={fetchNextPageForGroup}
                isFetchingNextPageByGroupId={isFetchingNextPageByGroupId}
                isFetching={isFetchingByGroupId[listing.groupId] ?? false}
              />
            ))}
          </Table.Body>
        </Table.Root>
      </Box>

      <FixedScrollbar
        scrollContainerRef={scrollContainerRef}
        isVisible={showFixedScrollbar}
        isTableBottomVisible={isTableBottomVisible}
      />
    </Box>
  );
};

export { GroupablePropertiesViewList };
export default GroupablePropertiesViewList;
